import LearningPathService from '@/services/learning-path-service';
import { StandardResponse } from '@/types/common';
import {
  CreateLearningPathPayload,
  LearningPath,
  PublishResponse,
  UpdateLearningPathPayload,
  UserLearningPathResponse,
} from '@/types/learning-path';
import {
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

// Learning Paths Query Keys
export const learningPathsKeys = {
  all: ['learning-paths'] as const,
  lists: () => [...learningPathsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) =>
    [...learningPathsKeys.lists(), { ...filters }] as const,
  details: () => [...learningPathsKeys.all, 'detail'] as const,
  detail: (id: number) => [...learningPathsKeys.details(), id] as const,
};

// My Learning Paths Query Keys
export const myLearningPathsKeys = {
  all: ['my-learning-paths'] as const,
  lists: () => [...myLearningPathsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) =>
    [...myLearningPathsKeys.lists(), { ...filters }] as const,
  details: () => [...myLearningPathsKeys.all, 'detail'] as const,
  detail: (id: number) => [...myLearningPathsKeys.details(), id] as const,
};

// Learning Paths Hooks
export const useLearningPaths = (filters?: Record<string, any>) => {
  return useQuery<StandardResponse<LearningPath>>({
    queryKey: learningPathsKeys.list(filters ?? {}),
    queryFn: () => LearningPathService.getLearningPaths(filters),
  });
};

export const useLearningPath = (
  id: number | undefined,
  options?: Omit<
    UseQueryOptions<LearningPath, Error, LearningPath, QueryKey>,
    'queryKey' | 'queryFn'
  >
) => {
  const queryKey = learningPathsKeys.detail(id as number);
  const queryFn = () => LearningPathService.getLearningPathById(id as number);
  const enabled = id !== undefined && (options?.enabled ?? true);

  return useQuery<LearningPath, Error, LearningPath, QueryKey>({
    queryKey,
    queryFn,
    enabled,
    ...options,
  });
};

export const useCreateLearningPath = () => {
  const queryClient = useQueryClient();

  return useMutation<LearningPath, Error, CreateLearningPathPayload>({
    mutationFn: (data: CreateLearningPathPayload) =>
      LearningPathService.createLearningPath(data),
    onSuccess: () => {
      toast.success('Learning path created successfully');
      queryClient.invalidateQueries({ queryKey: learningPathsKeys.lists() });
    },
    onError: (error: any) => {
      if (error.response?.data || error.response?.data?.errors) {
        const fieldErrors = error.response.data.errors || error.response?.data;
        const firstError = Object.values(fieldErrors)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          toast.error(firstError[0]);
        } else {
          const errorMessage =
            fieldErrors.error ||
            fieldErrors.detail ||
            'Failed to create learning path';
          toast.error(errorMessage);
        }
      } else {
        toast.error('Failed to create learning path');
      }
    },
  });
};

export const useUpdateLearningPath = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation<LearningPath, Error, UpdateLearningPathPayload>({
    mutationFn: (data: UpdateLearningPathPayload) =>
      LearningPathService.updateLearningPath(id, data),
    onSuccess: () => {
      toast.success('Learning path updated successfully');
      queryClient.invalidateQueries({ queryKey: learningPathsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: learningPathsKeys.lists() });
    },
    onError: (error: any) => {
      if (error.response?.data || error.response?.data?.errors) {
        const fieldErrors = error.response?.data || error.response.data.errors;
        const firstError = Object.values(fieldErrors)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          toast.error(firstError[0]);
        } else {
          const errorMessage =
            error.response?.data?.error ||
            error.response?.data?.detail ||
            'Failed to update learning path';
          toast.error(errorMessage);
        }
      } else {
        const errorMessage = 'Failed to update learning path';
        toast.error(errorMessage);
      }
    },
  });
};

export const useDeleteLearningPath = () => {
  const queryClient = useQueryClient();

  return useMutation<StandardResponse<LearningPath>, Error, number>({
    mutationFn: (id: number) => LearningPathService.deleteLearningPath(id),
    onSuccess: () => {
      toast.success('Learning path deleted successfully');
      queryClient.invalidateQueries({ queryKey: learningPathsKeys.lists() });
    },
    onError: (error: any) => {
      if (error.response?.data) {
        const data = error.response.data;
        const firstError = Object.values(data)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          toast.error(firstError[0]);
        } else {
          const errorMessage =
            data.error || data.detail || 'Failed to delete learning path';
          toast.error(errorMessage);
        }
      } else {
        toast.error('Failed to delete learning path');
      }
    },
  });
};

export const usePublishLearningPath = () => {
  const queryClient = useQueryClient();

  return useMutation<PublishResponse, Error, number>({
    mutationFn: (id: number) => LearningPathService.publishLearningPath(id),
    onSuccess: (_, id) => {
      toast.success('Learning path published successfully');
      queryClient.invalidateQueries({ queryKey: learningPathsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: learningPathsKeys.lists() });
    },
    onError: (error: any) => {
      if (error.response?.data) {
        const data = error.response.data;
        const firstError = Object.values(data)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          toast.error(firstError[0]);
        } else {
          const errorMessage =
            data.error || data.detail || 'Failed to publish learning path';
          toast.error(errorMessage);
        }
      } else {
        toast.error('Failed to publish learning path');
      }
    },
  });
};

// My Learning Paths Hooks
export const useMyLearningPaths = (
  filters?: {
    tag?: string;
    search?: string;
  },
  options?: { enabled?: boolean }
) => {
  return useQuery<UserLearningPathResponse>({
    queryKey: myLearningPathsKeys.list(filters ?? {}),
    queryFn: () => LearningPathService.getMyLearningPaths(filters),
    enabled: options?.enabled ?? true,
  });
};

export { useSoulMapLearningPath } from './useSoulMapLearningPath';
